/**
 * App - Main application component
 * Manages the assessment flow: Entry -> Assessment -> Results
 */

import React, { useEffect, useState } from 'react';
import { useAssessment } from './context/AssessmentContext';
import { colors, spacing, layout, fonts, radii, transitions } from './styles/theme';
import Header from './components/Header';
import EntryScreen from './components/EntryScreen';
import QuestionCard from './components/QuestionCard';
import Legend from './components/Legend';
import ResultsScreen from './components/ResultsScreen';
import LandingPage from './components/LandingPage';
import NamesScreen from './components/NamesScreen';
import IndicatorVisualizer from './components/IndicatorVisualizer';
import ExpertFeedback from './components/ExpertFeedback';
import FeedbackViewer from './components/FeedbackViewer';
import { Phase, AnswerValue, PHASE_LABELS } from './types';
import { 
    createSession, 
    fetchTree, 
    recordAnswer, 
    fetchReport, 
    fetchCart,
    undoLastAnswer
} from './services/api';
import {
    getNextAfterYes,
    getNextAfterNoOrCart,
    collectDescendantIds,
    countRemainingFromPath
} from './hooks/useTraversal';

const STACK_LEGEND_MQ = '(max-width: 767px)';

const App: React.FC = () => {
    const { state, dispatch } = useAssessment();

    const [stackAssessmentLegend, setStackAssessmentLegend] = useState(() =>
        typeof window !== 'undefined' && window.matchMedia(STACK_LEGEND_MQ).matches
    );

    useEffect(() => {
        const mq = window.matchMedia(STACK_LEGEND_MQ);
        const update = () => setStackAssessmentLegend(mq.matches);
        update();
        mq.addEventListener('change', update);
        return () => mq.removeEventListener('change', update);
    }, []);

    // Show feedback form when URL contains ?feedback
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.has('feedback')) {
            dispatch({ type: 'SET_VIEW', payload: 'feedback' });
        }
    }, [dispatch]);

    // Move from landing intro into names screen
    const handleBegin = () => {
        dispatch({ type: 'SET_VIEW', payload: 'names' });
    };

    // Go back from entry (or names) screen to landing intro
    const handleBackToBeginning = () => {
        dispatch({ type: 'SET_VIEW', payload: 'landing' });
    };

    // Handle starting the assessment
    const handleStart = async (phase: Phase, area: string) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_ERROR', payload: null });

        try {
            // Create session and fetch tree (with area so examples are embedded in each node)
            const [session, tree] = await Promise.all([
                createSession(phase, area),
                fetchTree(phase, area)
            ]);

            dispatch({ type: 'SET_PHASE', payload: phase });
            dispatch({ type: 'SET_AREA', payload: area });
            dispatch({
                type: 'START_SESSION',
                payload: { sessionId: session.id, tree, examples: {} }
            });
        } catch (error) {
            dispatch({
                type: 'SET_ERROR',
                payload: error instanceof Error ? error.message : 'Failed to start assessment'
            });
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    // Handle going back one question
    const handleBack = async () => {
        if (!state.sessionId || state.history.length === 0) return;
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            await undoLastAnswer(state.sessionId);
            dispatch({ type: 'GO_BACK' });
        } catch (err) {
            dispatch({ type: 'SET_ERROR', payload: err instanceof Error ? err.message : 'Failed to go back' });
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    // Handle answering a question
    const handleAnswer = async (value: AnswerValue) => {
        if (!state.currentNode || !state.sessionId) return;

        dispatch({ type: 'PUSH_HISTORY' });
        dispatch({ type: 'SET_LOADING', payload: true });

        try {
            // Record answer on backend
            await recordAnswer(state.sessionId, state.currentNode.id, value);

            // Record locally
            dispatch({
                type: 'RECORD_ANSWER',
                payload: {
                    questionId: state.currentNode.id,
                    value,
                    timestamp: new Date().toISOString()
                }
            });

            // Handle cart addition for add-to-cart answers
            if (value === 'add-to-cart') {
                const descendants = collectDescendantIds(state.currentNode);
                dispatch({
                    type: 'ADD_TO_CART',
                    payload: [state.currentNode.id, ...descendants]
                });
            }

            // Calculate next node based on answer
            let nextResult;
            if (value === 'yes') {
                nextResult = getNextAfterYes(state.tree, state.pathStack);
            } else {
                nextResult = getNextAfterNoOrCart(state.tree, state.pathStack);
            }

            if (nextResult.isComplete) {
                // Assessment complete - fetch results
                await loadResults();
            } else {
                // Move to next node
                dispatch({
                    type: 'SET_CURRENT_NODE',
                    payload: {
                        node: nextResult.node,
                        pathStack: nextResult.pathStack
                    }
                });
            }
        } catch (error) {
            dispatch({
                type: 'SET_ERROR',
                payload: error instanceof Error ? error.message : 'Failed to record answer'
            });
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    // Load final results
    const loadResults = async () => {
        if (!state.sessionId) return;

        try {
            const [reportData, cartData] = await Promise.all([
                fetchReport(state.sessionId),
                fetchCart(state.sessionId)
            ]);

            dispatch({
                type: 'SET_RESULTS',
                payload: {
                    report: reportData.report,
                    shoppingList: cartData.shoppingList
                }
            });
        } catch (error) {
            dispatch({
                type: 'SET_ERROR',
                payload: 'Failed to load results'
            });
        }
    };

    // Handle restart
    const handleRestart = () => {
        dispatch({ type: 'RESET' });
    };

    // Render based on current view
    if (state.view === 'feedback') {
        return <ExpertFeedback />;
    }

    if (state.view === 'feedback-admin') {
        return (
            <FeedbackViewer
                onBack={() => dispatch({ type: 'SET_VIEW', payload: 'landing' })}
            />
        );
    }

    if (state.view === 'visualizer') {
        return (
            <IndicatorVisualizer
                onBack={() => dispatch({ type: 'SET_VIEW', payload: 'landing' })}
            />
        );
    }

    if (state.view === 'landing') {
        return (
            <LandingPage
                onBegin={handleBegin}
                onOpenVisualizer={() => dispatch({ type: 'SET_VIEW', payload: 'visualizer' })}
                onOpenFeedbackViewer={() => dispatch({ type: 'SET_VIEW', payload: 'feedback-admin' })}
            />
        );
    }

    if (state.view === 'names') {
        return (
            <NamesScreen
                onContinue={(first, second) => {
                    dispatch({
                        type: 'SET_PARTICIPANTS',
                        payload: {
                            participantOneName: first,
                            participantTwoName: second,
                        },
                    });
                    dispatch({ type: 'SET_VIEW', payload: 'entry' });
                }}
                onBackToLanding={handleBackToBeginning}
                initialFirstPerson={state.participantOneName}
                initialSecondPerson={state.participantTwoName}
            />
        );
    }

    if (state.view === 'entry') {
        return (
            <EntryScreen
                onStart={handleStart}
                onBackToBeginning={handleBackToBeginning}
                participantOneName={state.participantOneName}
                participantTwoName={state.participantTwoName}
                isLoading={state.isLoading}
                error={state.error}
            />
        );
    }

    if (state.view === 'intro') {
        return (
            <div style={styles.appContainer}>
                <Header cartCount={0} showCart={false} />
                <main style={styles.introMain}>
                    <div style={styles.introCard}>
                        <h1 style={styles.introTitle}>Before you begin</h1>
                        <p style={styles.introSubtitle}>
                            You will be presented with a series of questions about ethical and legal aspects of your VR project.
                            For each question, you have three response options:
                        </p>

                        <div style={styles.introOptions}>
                            <div style={styles.introOption}>
                                <span style={{ ...styles.introOptionBadge, backgroundColor: colors.yes }}>
                                    Yes, I have good control over this
                                </span>
                                <p style={styles.introOptionText}>
                                    Choose this when you are confident that you have addressed this aspect adequately.
                                </p>
                            </div>
                            <div style={styles.introOption}>
                                <span style={{ ...styles.introOptionBadge, backgroundColor: colors.cart }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
                                        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                                        <line x1="3" y1="6" x2="21" y2="6" />
                                        <path d="M16 10a4 4 0 0 1-8 0" />
                                    </svg>
                                    Add to bag – I would like support
                                </span>
                                <p style={styles.introOptionText}>
                                    Choose this when you are unsure, or when this is a concern you would like guidance on.
                                    The item will be added to your bag with best practices and recommendations.
                                </p>
                            </div>
                            <div style={styles.introOption}>
                                <span style={{ ...styles.introOptionBadge, backgroundColor: colors.no }}>
                                    No, this is not relevant
                                </span>
                                <p style={styles.introOptionText}>
                                    Choose this when the topic does not apply to your project or situation.
                                </p>
                            </div>
                        </div>

                        <div style={styles.introNote}>
                            <strong>A note on self-assessment:</strong> Even when you feel confident about a topic,
                            consider whether your understanding has been formally reviewed or validated. It can be
                            valuable to add items to your bag for a second look, even if your initial instinct is "Yes."
                        </div>

                        <button
                            style={styles.introBeginButton}
                            onClick={() => dispatch({ type: 'SET_VIEW', payload: 'assessment' })}
                        >
                            Begin assessment
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: spacing[2] }}>
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                            </svg>
                        </button>
                    </div>
                </main>
            </div>
        );
    }

    if (state.view === 'assessment' && state.currentNode) {
        const exampleText = state.currentNode.example || '';
        const phaseLabel = state.phase ? PHASE_LABELS[state.phase] : '';
        const answered = state.answers.length;
        const remaining = countRemainingFromPath(state.tree, state.pathStack);
        const progressPct = answered + remaining > 0
            ? (answered / (answered + remaining)) * 100
            : 0;
        
        return (
            <div style={styles.appContainer}>
                <Header 
                    phase={phaseLabel}
                    area={state.area}
                    cartCount={state.userCartCount}
                    cartIds={state.cartIds}
                    tree={state.tree}
                />

                {/* Progress bar + early checkout */}
                <div style={styles.progressStrip}>
                    <div style={styles.progressInner}>
                        <div style={styles.progressTrack} aria-label={`Progress: approximately ${Math.round(progressPct)}%`}>
                            <div style={{ ...styles.progressFill, width: `${progressPct}%` }} />
                        </div>
                        {answered > 0 && (
                            <button
                                style={styles.checkoutButton}
                                onClick={() => void loadResults()}
                                disabled={state.isLoading}
                                aria-label="Finish assessment early and view results"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
                                    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                                    <line x1="3" y1="6" x2="21" y2="6" />
                                    <path d="M16 10a4 4 0 0 1-8 0" />
                                </svg>
                                Finish and view results
                            </button>
                        )}
                    </div>
                </div>
                
                <main style={{
                    ...styles.main,
                    ...(stackAssessmentLegend ? styles.mainNarrow : {}),
                }}>
                    <div style={{
                        ...styles.contentWrapper,
                        ...(stackAssessmentLegend ? styles.contentWrapperStacked : {}),
                    }}>
                        <div style={{
                            ...styles.cardWrapper,
                            ...(stackAssessmentLegend ? styles.cardWrapperStacked : {}),
                        }}>
                            <QuestionCard
                                questionText={state.currentNode.question}
                                exampleText={exampleText}
                                cartCount={state.userCartCount}
                                isLoading={state.isLoading}
                                onYes={() => handleAnswer('yes')}
                                onNo={() => handleAnswer('no')}
                                onAddToCart={() => handleAnswer('add-to-cart')}
                                onBack={state.history.length > 0 ? handleBack : undefined}
                                onRestart={handleRestart}
                            />
                        </div>
                        <Legend hasExample={!!exampleText} stackBelow={stackAssessmentLegend} />
                    </div>

                    {state.error && (
                        <div style={styles.error}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            <span>{state.error}</span>
                        </div>
                    )}
                </main>
            </div>
        );
    }

    if (state.view === 'results' && state.phase) {
        return (
            <ResultsScreen
                phase={state.phase}
                area={state.area}
                report={state.report}
                shoppingList={state.shoppingList}
                onRestart={handleRestart}
                onGiveFeedback={() => dispatch({ type: 'SET_VIEW', payload: 'feedback' })}
                participantOneName={state.participantOneName}
                participantTwoName={state.participantTwoName}
            />
        );
    }

    // Fallback loading state
    return (
        <div style={styles.loading}>
            Loading...
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    appContainer: {
        minHeight: '100vh',
        backgroundColor: colors.bgLight,
        display: 'flex',
        flexDirection: 'column',
    },
    main: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: spacing[6],
    },
    mainNarrow: {
        padding: `${spacing[4]} ${spacing[3]}`,
    },
    contentWrapper: {
        display: 'flex',
        gap: spacing[4],
        width: '100%',
        maxWidth: '1200px',
        alignItems: 'flex-start',
        justifyContent: 'center',
    },
    contentWrapperStacked: {
        flexDirection: 'column' as const,
        alignItems: 'stretch' as const,
    },
    cardWrapper: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        maxWidth: layout.maxWidth,
    },
    cardWrapperStacked: {
        flex: 'none',
        width: '100%',
        maxWidth: '100%',
        alignItems: 'stretch' as const,
    },
    error: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing[2],
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        color: colors.no,
        padding: `${spacing[3]} ${spacing[4]}`,
        borderRadius: '6px',
        marginTop: spacing[4],
        fontSize: fonts.sm,
    },
    progressStrip: {
        backgroundColor: colors.white,
        borderBottom: `1px solid ${colors.border}`,
        padding: `${spacing[3]} ${spacing[6]}`,
    },
    progressInner: {
        maxWidth: layout.maxWidth,
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        gap: spacing[4],
    },
    progressTrack: {
        flex: 1,
        height: '8px',
        backgroundColor: colors.bgWarm,
        borderRadius: radii.full,
        overflow: 'hidden' as const,
    },
    progressFill: {
        height: '100%',
        backgroundColor: colors.yes,
        borderRadius: radii.full,
        transition: 'width 0.4s ease',
    },
    checkoutButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${spacing[2]} ${spacing[4]}`,
        fontSize: fonts.sm,
        fontWeight: fonts.semibold,
        color: colors.white,
        backgroundColor: colors.cart,
        border: 'none',
        borderRadius: radii.md,
        cursor: 'pointer',
        transition: `all ${transitions.fast}`,
        whiteSpace: 'nowrap' as const,
        flexShrink: 0,
    },
    loading: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: fonts.lg,
        color: colors.textSecondary,
        backgroundColor: colors.bgLight,
    },
    introMain: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: spacing[6],
    },
    introCard: {
        width: '100%',
        maxWidth: '640px',
        backgroundColor: colors.white,
        border: `1px solid ${colors.border}`,
        borderRadius: radii.xl,
        padding: spacing[8],
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
    },
    introTitle: {
        fontSize: fonts['2xl'],
        fontWeight: fonts.bold,
        color: colors.text,
        marginBottom: spacing[3],
    },
    introSubtitle: {
        fontSize: fonts.base,
        color: colors.textSecondary,
        lineHeight: fonts.lineHeightRelaxed,
        marginBottom: spacing[6],
    },
    introOptions: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: spacing[4],
        marginBottom: spacing[6],
    },
    introOption: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: spacing[2],
    },
    introOptionBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        alignSelf: 'flex-start',
        padding: `${spacing[2]} ${spacing[3]}`,
        fontSize: fonts.sm,
        fontWeight: fonts.semibold,
        color: colors.white,
        borderRadius: radii.md,
    },
    introOptionText: {
        fontSize: fonts.sm,
        color: colors.textSecondary,
        lineHeight: fonts.lineHeightRelaxed,
        margin: 0,
        paddingLeft: spacing[1],
    },
    introNote: {
        fontSize: fonts.sm,
        color: colors.text,
        lineHeight: fonts.lineHeightRelaxed,
        backgroundColor: colors.bgWarm,
        padding: spacing[4],
        borderRadius: radii.lg,
        marginBottom: spacing[6],
        border: `1px solid ${colors.border}`,
    },
    introBeginButton: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${spacing[4]} ${spacing[6]}`,
        fontSize: fonts.lg,
        fontWeight: fonts.bold,
        color: colors.white,
        backgroundColor: colors.primary,
        border: 'none',
        borderRadius: radii.lg,
        cursor: 'pointer',
        transition: `all ${transitions.fast}`,
        minHeight: layout.minTouchTarget,
    },
};

export default App;